import React, { useEffect, useMemo, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useAuth } from "../AuthProvider.tsx";
import PortalSidebar from "../components/PortalSidebar.tsx";
import { buildApiUrl } from "../services/apiConfig.ts";
import "./overview.css";
import { Line } from "react-chartjs-2";
import {
  Chart,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
  Filler,
  ChartData,
} from "chart.js";
import { MapContainer, TileLayer, useMap } from "react-leaflet";
import "leaflet/dist/leaflet.css";
import L from "leaflet";
import "leaflet.heat";

Chart.register(CategoryScale, LinearScale, PointElement, LineElement, Tooltip, Legend, Filler);

type HeatmapPoint = {
  lat: number;
  lng: number;
  weight: number;
  first_scan: string | null;
  last_scan: string | null;
};

type HeatmapPayload = {
  ad_id: string;
  total_scans: number;
  matched_scans: number;
  points: HeatmapPoint[];
  centroid: { lat: number; lng: number } | null;
  ad_start: string | null;
  ad_end: string | null;
};

type HeatmapRenderPoint = HeatmapPoint;

type DriverPathSegment = [number, number][];

type DriverPath = {
  id: string;
  carId: string | null;
  startTimestamp: string | null;
  endTimestamp: string | null;
  assignmentStart: string | null;
  assignmentEnd: string | null;
  segments: DriverPathSegment[];
};

const DEFAULT_QR_CENTER: [number, number] = [47.3769, 8.5417];
const DEFAULT_IMP_CENTER: [number, number] = [46.948, 7.4474];
const DEFAULT_QR_ZOOM = 11;
const DEFAULT_QR_FULLSCREEN_ZOOM = 12;
const DEFAULT_PATH_ZOOM = 11;

function HeatmapLayer({ points }: { points: HeatmapRenderPoint[] }) {
  const map = useMap();

  useEffect(() => {
    if (!map || !points.length) {
      return undefined;
    }

    const maxWeight = points.reduce((acc, point) => Math.max(acc, point.weight), 0);
    const heatData = points.map(
      (point) => [point.lat, point.lng, point.weight] as [number, number, number],
    );

    const heatLayer = (L as any).heatLayer(heatData, {
      radius: 28,
      blur: 22,
      maxZoom: 15,
      minOpacity: 0.25,
      max: Math.max(maxWeight, 1),
      gradient: {
        0.2: "#2563eb",
        0.4: "#22c55e",
        0.6: "#eab308",
        0.8: "#f97316",
        1.0: "#dc2626",
      },
    });

    heatLayer.addTo(map);
    return () => {
      map.removeLayer(heatLayer);
    };
  }, [map, points]);

  return null;
}

function HeatmapStatusOverlay({
  loading,
  error,
  hasData,
  totalScans,
  matchedScans,
}: {
  loading: boolean;
  error: string | null;
  hasData: boolean;
  totalScans: number;
  matchedScans: number;
}) {
  if (loading) {
    return <div className="portal-map-overlay">Heatmap wird geladen…</div>;
  }
  if (error) {
    return <div className="portal-map-overlay error">Heatmap konnte nicht geladen werden</div>;
  }
  if (!hasData) {
    if (totalScans > 0 && matchedScans === 0) {
      return <div className="portal-map-overlay">Scans vorhanden, aber keine Fahrtdaten gefunden</div>;
    }
    return <div className="portal-map-overlay">Noch keine QR-Scans vorhanden</div>;
  }
  return null;
}

function PathStatusOverlay({
  loading,
  error,
  hasData,
}: {
  loading: boolean;
  error: string | null;
  hasData: boolean;
}) {
  if (loading) {
    return <div className="portal-map-overlay">Fahrspuren werden geladen…</div>;
  }
  if (error) {
    return <div className="portal-map-overlay error">Fahrspuren konnten nicht geladen werden</div>;
  }
  if (!hasData) {
    return <div className="portal-map-overlay">Noch keine Fahrten aufgezeichnet</div>;
  }
  return null;
}

// Generate nice-looking fake timeseries data
function genSeries(points = 120, base = 1000, volatility = 0.06) {
  let v = Math.random() * base * 0.05;
  const arr: number[] = [];
  for (let i = 0; i < points; i++) {
    const drift = base * (i / points) * 0.9; // upward drift
    v = Math.max(0, v + (Math.random() - 0.5) * base * volatility);
    arr.push(Math.round(drift + v));
  }
  return arr;
}

function gradientFill(ctx: CanvasRenderingContext2D, area: any) {
  const grad = ctx.createLinearGradient(0, area.bottom, 0, area.top);
  grad.addColorStop(0, "rgba(59,130,246,0.35)"); // blue
  grad.addColorStop(0.5, "rgba(234,179,8,0.28)"); // amber
  grad.addColorStop(1, "rgba(244,63,94,0.35)"); // rose
  return grad;
}

export default function CampaignDetail() {
  const { id } = useParams<{ id: string }>();
  const { fetchWithAuth } = useAuth();
  const navigate = useNavigate();

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [metrics, setMetrics] = useState<any>(null);
  const [adBudget, setAdBudget] = useState<number | null>(null);
  const [fullscreenMap, setFullscreenMap] = useState<null | "qr" | "imp">(null);
  const [qrHeatmap, setQrHeatmap] = useState<HeatmapPayload | null>(null);
  const [heatmapLoading, setHeatmapLoading] = useState(false);
  const [heatmapError, setHeatmapError] = useState<string | null>(null);
  const [driverPaths, setDriverPaths] = useState<DriverPath[]>([]);
  const [pathsLoading, setPathsLoading] = useState(false);
  const [pathsError, setPathsError] = useState<string | null>(null);

  // Fetch metrics for the selected ad
  useEffect(() => {
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const res = await fetchWithAuth(buildApiUrl(`/ad/metrics?ad_id=${encodeURIComponent(id ?? "")}`));
        if (!res.ok) throw new Error(`Failed to fetch metrics: ${res.status}`);
        const json = await res.json();
        setMetrics(json.metrics);
      } catch (e: any) {
        setError(e.message || String(e));
      } finally {
        setLoading(false);
      }
    })();
  }, [id, fetchWithAuth]);

  useEffect(() => {
    if (!id) {
      setHeatmapLoading(false);
      setHeatmapError(null);
      setQrHeatmap(null);
      return;
    }

    let cancelled = false;
    setHeatmapLoading(true);
    setHeatmapError(null);
    setQrHeatmap(null);

    (async () => {
      try {
        const res = await fetchWithAuth(buildApiUrl(`/ad/heatmap?ad_id=${encodeURIComponent(id)}`));
        if (!res.ok) {
          throw new Error(`Heatmap request failed (${res.status})`);
        }
        const json = await res.json();
        if (!cancelled) {
          setQrHeatmap(json?.heatmap ?? null);
        }
      } catch (e) {
        console.error("Failed to fetch QR heatmap", e);
        if (!cancelled) {
          setHeatmapError("Heatmap konnte nicht geladen werden");
        }
      } finally {
        if (!cancelled) {
          setHeatmapLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, fetchWithAuth]);

  useEffect(() => {
    if (!id) {
      setDriverPaths([]);
      setPathsLoading(false);
      setPathsError(null);
      return;
    }

    let cancelled = false;
    setPathsLoading(true);
    setPathsError(null);

    (async () => {
      try {
        const res = await fetchWithAuth(buildApiUrl(`/ad/paths?ad_id=${encodeURIComponent(id)}`));
        if (!res.ok) {
          throw new Error(`Paths request failed (${res.status})`);
        }
        const json = await res.json();
        if (cancelled) {
          return;
        }

        const items = Array.isArray(json?.paths?.items) ? json.paths.items : [];
        const sanitized: DriverPath[] = [];

        for (const item of items) {
          if (!item) continue;

          const segmentsRaw = Array.isArray(item.segments) ? item.segments : [];
          const segments: DriverPathSegment[] = [];

          for (const seg of segmentsRaw) {
            if (!Array.isArray(seg)) continue;
            const coords: DriverPathSegment = [];
            for (const point of seg) {
              if (!point) continue;
              const lat = Number(point.lat ?? point[0]);
              const lng = Number(point.lng ?? point[1]);
              if (!Number.isFinite(lat) || !Number.isFinite(lng)) continue;
              coords.push([lat, lng]);
            }
            if (coords.length >= 2) {
              segments.push(coords);
            }
          }

          if (!segments.length) {
            continue;
          }

          sanitized.push({
            id: String(item.id ?? ""),
            carId: item.car_id ? String(item.car_id) : null,
            startTimestamp: item.start_timestamp ?? null,
            endTimestamp: item.end_timestamp ?? null,
            assignmentStart: item.assignment_start ?? null,
            assignmentEnd: item.assignment_end ?? null,
            segments,
          });
        }

        setDriverPaths(sanitized);
      } catch (e) {
        console.error("Failed to fetch driver paths", e);
        if (!cancelled) {
          setPathsError("Fahrspuren konnten nicht geladen werden");
          setDriverPaths([]);
        }
      } finally {
        if (!cancelled) {
          setPathsLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [id, fetchWithAuth]);

  // Fetch ad list and pick current ad to get budget (until a dedicated by-id route exists)
  useEffect(() => {
    (async () => {
      try {
        const res = await fetchWithAuth(buildApiUrl("/ad"));
        if (!res.ok) return;
        const json = await res.json();
        const ad = (json.ads || []).find((x: any) => String(x.id) === String(id));
        if (ad) setAdBudget(Number(ad.budget) || 0);
      } catch {}
    })();
  }, [id, fetchWithAuth]);

  // Labels based on metrics if available
  const labels = useMemo(() => {
    if (metrics?.timeseries?.length) {
      return metrics.timeseries.map((d: any) => d.date);
    }
    const startIso: string | null | undefined = metrics?.ad_start ?? null;
    const endIso: string | null | undefined = metrics?.ad_end ?? null;
    if (startIso) {
      const start = new Date(startIso);
      const end = endIso ? new Date(endIso) : new Date();
      if (!Number.isNaN(start.valueOf())) {
        if (Number.isNaN(end.valueOf()) || end < start) {
          end.setTime(start.getTime());
        }
        start.setHours(0, 0, 0, 0);
        end.setHours(0, 0, 0, 0);
        const range: string[] = [];
        const cursor = new Date(start);
        while (cursor <= end) {
          const iso = cursor.toISOString().slice(0, 10);
          range.push(iso);
          cursor.setDate(cursor.getDate() + 1);
        }
        return range;
      }
    }
    return [];
  }, [metrics]);

  // Reusable chart options
  const baseOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: { legend: { display: false }, tooltip: { mode: "index", intersect: false } },
    elements: { point: { radius: 0 } },
    scales: {
      x: { grid: { display: false } },
      y: {
        beginAtZero: true,
        grid: { color: "rgba(0,0,0,0.06)" },
        ticks: {
          callback: (v: any) => {
            const n = Number(v);
            if (!isFinite(n)) return String(v);
            if (Math.abs(n) >= 1000) return `${Math.round(n / 100) / 10}k`;
            return `${n}`;
          },
        },
      },
    },
  } as const;

  const makeData = (series: number[]) => ({
    labels,
    datasets: [
      {
        data: series,
        borderColor: "#0b8a6d",
        backgroundColor: "rgba(11, 138, 109, 0.20)",
        fill: true,
        tension: 0.35,
      },
    ],
  }) as ChartData<"line">;

  // Build series using real metrics where available
  const impressionsSeries = useMemo(() => {
    if (metrics?.timeseries?.length) {
      // Use inferred impressions as provided by backend (view/km based)
      return metrics.timeseries.map((d: any) => Number(d.impressions_inferred ?? 0) || 0);
    }
    // fallback: no data yet
    return Array.from({ length: labels.length }, () => 0);
  }, [metrics, labels.length]);

  const visitorsSeries = useMemo(() => {
    if (metrics?.timeseries?.length) {
      return metrics.timeseries.map((d: any) => Number(d.visitors) || 0);
    }
    return Array.from({ length: labels.length }, () => 0);
  }, [metrics, labels.length]);

  const carsSeries = useMemo(() => {
    if (metrics?.timeseries?.length) {
      return metrics.timeseries.map((d: any) => Number(d?.active_cars ?? 0));
    }
    return Array.from({ length: labels.length }, () => 0);
  }, [metrics, labels.length]);

  // Actual budget usage series (cumulative) from API timeseries.budget_used
  const spendSeries = useMemo(() => {
    const n = labels.length;
    if (metrics?.timeseries?.length) {
      let cum = 0;
      return metrics.timeseries.map((d: any) => {
        const v = Number(d.budget_used) || 0;
        cum += v;
        return Math.round(cum);
      });
    }
    // fallback: unknown -> zeros
    return Array.from({ length: n }, () => 0);
  }, [metrics, labels.length]);

  const qrSeries = useMemo(() => {
    // Use unique visitors to align with Plausible UI when filtering by car_ads
    return visitorsSeries;
  }, [visitorsSeries]);

  const qrHeatmapPoints = useMemo<HeatmapRenderPoint[]>(() => {
    if (!qrHeatmap?.points?.length) {
      return [];
    }

    const sanitized: HeatmapPoint[] = [];
    for (const rawPoint of qrHeatmap.points) {
      const lat = Number(rawPoint.lat);
      const lng = Number(rawPoint.lng);
      const weight = Number(rawPoint.weight) || 0;
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
        continue;
      }
      sanitized.push({
        lat,
        lng,
        weight,
        first_scan: rawPoint.first_scan ?? null,
        last_scan: rawPoint.last_scan ?? null,
      });
    }

    if (!sanitized.length) {
      return [];
    }

    return sanitized;
  }, [qrHeatmap]);

  const qrHasData = qrHeatmapPoints.length > 0;

  const qrMapCenter = useMemo<[number, number]>(() => {
    if (qrHeatmap?.centroid) {
      const { lat, lng } = qrHeatmap.centroid;
      if (Number.isFinite(lat) && Number.isFinite(lng)) {
        return [lat, lng];
      }
    }
    if (qrHeatmapPoints.length) {
      return [qrHeatmapPoints[0].lat, qrHeatmapPoints[0].lng];
    }
    return DEFAULT_QR_CENTER;
  }, [qrHeatmap, qrHeatmapPoints]);

  const qrMapZoom = qrHasData ? 12 : DEFAULT_QR_ZOOM;
  const qrFullscreenZoom = qrHasData ? 13 : DEFAULT_QR_FULLSCREEN_ZOOM;
  const qrTotalScans = qrHeatmap?.total_scans ?? 0;
  const qrMatchedScans = qrHeatmap?.matched_scans ?? 0;

  const qrCoveragePercent = useMemo<number | null>(() => {
    if (!qrTotalScans) return null;
    return Math.round((qrMatchedScans / qrTotalScans) * 100);
  }, [qrMatchedScans, qrTotalScans]);

  const pathHeatmapPoints = useMemo<HeatmapRenderPoint[]>(() => {
    const points: HeatmapRenderPoint[] = [];
    for (const path of driverPaths) {
      for (const segment of path.segments) {
        for (let i = 0; i < segment.length - 1; i++) {
          const [lat1, lng1] = segment[i];
          const [lat2, lng2] = segment[i + 1];
          if (!Number.isFinite(lat1) || !Number.isFinite(lng1) || !Number.isFinite(lat2) || !Number.isFinite(lng2)) {
            continue;
          }
          const midLat = (lat1 + lat2) / 2;
          const midLng = (lng1 + lng2) / 2;
          const deltaLat = lat2 - lat1;
          const deltaLng = lng2 - lng1;
          const approxDist = Math.sqrt(deltaLat * deltaLat + deltaLng * deltaLng) * 111_000;
          const weight = Math.max(approxDist / 50, 0.75);
          points.push({
            lat: midLat,
            lng: midLng,
            weight,
            first_scan: null,
            last_scan: null,
          });
        }
      }
    }
    return points;
  }, [driverPaths]);

  const pathHasData = pathHeatmapPoints.length > 0;

  const pathMapCenter = useMemo<[number, number]>(() => {
    if (pathHeatmapPoints.length) {
      return [pathHeatmapPoints[0].lat, pathHeatmapPoints[0].lng];
    }
    for (const path of driverPaths) {
      for (const segment of path.segments) {
        if (segment.length) {
          return [segment[0][0], segment[0][1]];
        }
      }
    }
    return DEFAULT_IMP_CENTER;
  }, [driverPaths, pathHeatmapPoints]);

  const pathMapZoom = pathHasData ? 12 : DEFAULT_PATH_ZOOM;

  const effectiveCpmSeries = useMemo(() => {
    // CPM = (cumulative spend / cumulative impressions) * 1000
    let cumImp = 0;
    return impressionsSeries.map((imp, idx) => {
      cumImp += Math.max(0, imp);
      const cost = spendSeries[Math.min(idx, spendSeries.length - 1)] || 0;
      const cpm = cumImp > 0 ? (cost / cumImp) * 1000 : 0;
      return Math.round(cpm);
    });
  }, [impressionsSeries, spendSeries]);

  return (
    <div className="portal-root">
      <PortalSidebar />
      <main className="portal-main">
        <div className="portal-topbar portal-topbar-wide">
          <div className="portal-title">Echtzeit Analyse</div>
          <button className="btn light" onClick={() => navigate("/")}>Zur Übersicht</button>
        </div>

        {loading && <div>Loading…</div>}
        {error && <div style={{background:'#c14725',padding:12,borderRadius:12}}>{error}</div>}

        {!loading && !error && (
          <>
            <section className="portal-charts">
              <div className="portal-chart-panel">
                <div className="portal-chart-title">Impressionen</div>
                <div className="portal-chart-wrap">
                  <Line data={makeData(impressionsSeries)} options={baseOptions} />
                </div>
              </div>

              
              <div className="portal-chart-panel">
                <div className="portal-chart-title">QR Code Scans</div>
                <div className="portal-chart-wrap">
                  <Line data={makeData(qrSeries)} options={baseOptions} />
                </div>
              </div>

              
              <div className="portal-chart-panel">
                <div className="portal-chart-title">Effektive TKP</div>
                <div className="portal-chart-wrap">
                  <Line data={makeData(effectiveCpmSeries)} options={baseOptions} />
                </div>
              </div>

              
              <div className="portal-chart-panel">
                <div className="portal-chart-title">Werbende Fahrzeuge</div>
                <div className="portal-chart-wrap">
                  <Line data={makeData(carsSeries)} options={baseOptions} />
                </div>
              </div>

              
              <div className="portal-chart-panel">
                <div className="portal-chart-title">Budget Nutzung</div>
                <div className="portal-chart-wrap">
                  <Line data={makeData(spendSeries)} options={baseOptions} />
                </div>
              </div>
            </section>

            
            <section className="portal-charts">
              <div className="portal-chart-panel">
                <div className="portal-panel-top">
                  <div className="portal-chart-title">QRCode Scan Heatmap</div>
                  <button className="btn tiny light" onClick={() => setFullscreenMap("qr")}>Maximieren</button>
                </div>
                <div className="portal-map">
                  <MapContainer
                    key={`qr-map-${qrMapCenter[0].toFixed(3)}-${qrMapCenter[1].toFixed(3)}-${qrHeatmapPoints.length}`}
                    center={qrMapCenter}
                    zoom={qrMapZoom}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                    <HeatmapLayer points={qrHeatmapPoints} />
                  </MapContainer>
                  <HeatmapStatusOverlay
                    loading={heatmapLoading}
                    error={heatmapError}
                    hasData={qrHasData}
                    totalScans={qrTotalScans}
                    matchedScans={qrMatchedScans}
                  />
                </div>
                {qrTotalScans > 0 && (
                  <div className="portal-map-caption">
                    Zuordnung: {qrMatchedScans}/{qrTotalScans} Scans
                    {typeof qrCoveragePercent === "number" ? ` (${qrCoveragePercent}%)` : ""}
                  </div>
                )}
              </div>
              <div className="portal-chart-panel">
                <div className="portal-panel-top">
                  <div className="portal-chart-title">Gefahrene Strecken</div>
                  <button className="btn tiny light" onClick={() => setFullscreenMap("imp")}>Maximieren</button>
                </div>
                <div className="portal-map">
                  <MapContainer
                    key={`path-map-${pathMapCenter[0].toFixed(3)}-${pathMapCenter[1].toFixed(3)}-${pathHeatmapPoints.length}`}
                    center={pathMapCenter}
                    zoom={pathMapZoom}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                    <HeatmapLayer points={pathHeatmapPoints} />
                  </MapContainer>
                  <PathStatusOverlay loading={pathsLoading} error={pathsError} hasData={pathHasData} />
                </div>
                {pathHasData && (
                  <div className="portal-map-caption">
                    Sichtbare Fahrten: {driverPaths.length}
                  </div>
                )}
              </div>
            </section>

          </>
        )}
      </main>
      
      {fullscreenMap && (
        <div className="portal-fs-backdrop" onClick={() => setFullscreenMap(null)}>
          <div className="portal-fs-panel" onClick={(e)=>e.stopPropagation()}>
            <div className="portal-fullscreen-top">
              <div className="portal-fs-title">{fullscreenMap === "qr" ? "QRCode Scan Heatmap" : "Gefahrene Strecken"}</div>
              <button className="btn tiny dark" onClick={() => setFullscreenMap(null)}>Schließen</button>
            </div>
            <div className="portal-fs-map">
              {fullscreenMap === "qr" ? (
                <>
                  <MapContainer
                    key={`qr-map-full-${qrMapCenter[0].toFixed(3)}-${qrMapCenter[1].toFixed(3)}-${qrHeatmapPoints.length}`}
                    center={qrMapCenter}
                    zoom={qrFullscreenZoom}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                    <HeatmapLayer points={qrHeatmapPoints} />
                  </MapContainer>
                  <HeatmapStatusOverlay
                    loading={heatmapLoading}
                    error={heatmapError}
                    hasData={qrHasData}
                    totalScans={qrTotalScans}
                    matchedScans={qrMatchedScans}
                  />
                </>
              ) : (
                <>
                  <MapContainer
                    key={`path-map-full-${pathMapCenter[0].toFixed(3)}-${pathMapCenter[1].toFixed(3)}-${pathHeatmapPoints.length}`}
                    center={pathMapCenter}
                    zoom={Math.max(pathMapZoom, 12)}
                    style={{ height: "100%", width: "100%" }}
                  >
                    <TileLayer url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png" attribution="&copy; OpenStreetMap" />
                    <HeatmapLayer points={pathHeatmapPoints} />
                  </MapContainer>
                  <PathStatusOverlay loading={pathsLoading} error={pathsError} hasData={pathHasData} />
                </>
              )}
            </div>
            {fullscreenMap === "qr" && qrTotalScans > 0 && (
              <div className="portal-map-caption portal-map-caption-fs">
                Zuordnung: {qrMatchedScans}/{qrTotalScans} Scans
                {typeof qrCoveragePercent === "number" ? ` (${qrCoveragePercent}%)` : ""}
              </div>
            )}
            {fullscreenMap === "imp" && pathHasData && (
              <div className="portal-map-caption portal-map-caption-fs">
                Sichtbare Fahrten: {driverPaths.length}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
